import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { PatientPortalService } from '../patient-portal.service';
import { FileManagerService } from '../../file-manager/file-manager.service';
import { DOCUMENT_TAG } from 'src/app/models/constants/document-tag.model';
import { UiService } from 'src/app/services/ui.service';

// Shape of the JSON blob stored in dataINFGrInsulranceCard.InsuranceCardInfo.
// InsuranceHasInsurance is bound to the "I do not have insurance" checkbox, so true means the patient
// declared they have no insurance.
export interface InsuranceCardInfoModel {
  InsuranceHasInsurance: boolean;
  InsuranceCompanyName: string;
  InsuranceAddress: string;
  InsurancePhone: string;
  InsuranceGroup: string;
  InsuranceID: string;
  InsuranceEmployer: string;
  SubscriberName: string;
  SubscriberSSN: string;
  SubscriberDOB: string;
}

@Component({
  standalone: false,
  selector: 'app-patient-portal-insurance',
  templateUrl: './patient-portal-insurance.component.html',
  styleUrls: ['./patient-portal-insurance.component.css']
})
export class PatientPortalInsuranceComponent implements OnInit {

  @Input() disableSaveButton: boolean = false;
  @Input() PatientId: string;
  @Input() imageViewWidth: number = 0;
  @Input() useImageViewWidth: boolean = false;

  cardObj: any;
  tmpCardObj: InsuranceCardInfoModel = PatientPortalInsuranceComponent.createEmptyCardInfo();
  isLoading: boolean = false;

  documentGroup: any;
  documentParentObjectName: string = "dataINFGrInsulranceCard";
  DOCUMENT_TAG: DOCUMENT_TAG;
  @ViewChild('imageView') imageView: any;

  constructor(
    private readonly infoService: PatientPortalService,
    private readonly fileService: FileManagerService,
    private readonly uiService: UiService,
  ) {
    this.DOCUMENT_TAG = DOCUMENT_TAG;
  }

  private static createEmptyCardInfo(): InsuranceCardInfoModel {
    return {
      InsuranceHasInsurance: false,
      InsuranceCompanyName: '',
      InsuranceAddress: '',
      InsurancePhone: '',
      InsuranceGroup: '',
      InsuranceID: '',
      InsuranceEmployer: '',
      SubscriberName: '',
      SubscriberSSN: '',
      SubscriberDOB: '',
    };
  }

  // Reads the stored blob defensively: a malformed or partial blob falls back to an empty card rather
  // than throwing, and every missing field gets a default of its own type.
  private static parseCardInfo(rawInfo: string): InsuranceCardInfoModel {
    const card = PatientPortalInsuranceComponent.createEmptyCardInfo();

    if (!rawInfo) {
      return card;
    }

    let stored: Partial<InsuranceCardInfoModel>;
    try {
      stored = JSON.parse(rawInfo);
    } catch (err) {
      console.error('Unable to parse stored insurance card info', err);
      return card;
    }

    if (!stored || typeof stored !== 'object') {
      return card;
    }

    card.InsuranceHasInsurance = stored.InsuranceHasInsurance === true;
    card.InsuranceCompanyName = stored.InsuranceCompanyName ?? '';
    card.InsuranceAddress = stored.InsuranceAddress ?? '';
    card.InsurancePhone = stored.InsurancePhone ?? '';
    card.InsuranceGroup = stored.InsuranceGroup ?? '';
    card.InsuranceID = stored.InsuranceID ?? '';
    card.InsuranceEmployer = stored.InsuranceEmployer ?? '';
    card.SubscriberName = stored.SubscriberName ?? '';
    card.SubscriberSSN = stored.SubscriberSSN ?? '';
    card.SubscriberDOB = stored.SubscriberDOB ?? '';

    return card;
  }

  ngOnInit() {
    this.isLoading = true;
    this.getPatientInsuranceCard()
      .then((card: any) => {
        this.cardObj = card;
        this.tmpCardObj = PatientPortalInsuranceComponent.parseCardInfo(card?.InsuranceCardInfo);

        this.getDocumentGroupByType()
          .then((documentGroups: any[]) => {

            if (documentGroups.length) {
              this.documentGroup = documentGroups[0];
              this.isLoading = false;
            } else {
              this.createDocumentGroup()
                .then((documentGroup: any) => {
                  this.documentGroup = documentGroup;
                  this.isLoading = false;
                }, err => {
                  this.isLoading = false;
                });
            }

          }, err => {
            this.isLoading = false;
          });

      }, err => {
        this.isLoading = false;
      });
  }

  getFileViewWidth() {

    if (this.useImageViewWidth) {
      return this.imageViewWidth - 50;
    }

    try {
      var imageViewSize = document.getElementById('mainInsuranceContainer').offsetWidth;
      return imageViewSize;
    } catch (err) {
      console.log('ERROR', err);
      return 0;
    }
  }

  activateImageView() {
    this.imageView?.handleUploadClicked();
  }

  getDocumentGroupByType() {
    let promise = new Promise((resolve, reject) => {
      this.fileService.getDocumentGroupByObjectId(this.PatientId, this.cardObj.InfGrInsuranceID)
        .subscribe(resolve, reject);
    });
    return promise;
  }

  createDocumentGroup() {
    let promise = new Promise((resolve, reject) => {
      var creationObj = {
        ParentObjectName: this.documentParentObjectName,
        ObjectId: this.cardObj.InfGrInsuranceID,
        PatientId: this.PatientId,
        documentIds: [],
      };
      this.fileService.createDocumentGroup(creationObj)
        .subscribe(resolve, reject);
    });
    return promise;
  }

  public saveFromAllWindows() {
    let promise = new Promise((resolve, reject) => {
      this.savePatientInsuranceCard()
        .then(resolve, reject);
    });
    return promise;
  }

  onSavePressed() {
    this.isLoading = true;
    this.savePatientInsuranceCard()
      .then(() => {
        this.isLoading = false;
        this.uiService.displayMessage('Success', 'Insurance information saved.');
      }, err => {
        this.isLoading = false;
        this.reportSaveFailure(err);
      });
  }

  // A save is never allowed to fail quietly: the patient must know their answers did not reach us.
  private reportSaveFailure(err: any) {
    console.error('Unable to save the patient insurance card', err);

    if (err instanceof HttpErrorResponse && err.status === 409) {
      this.uiService.displayMessage('Error', 'This insurance information was updated somewhere else while you were editing it. Please reload the page and enter your changes again.');
      return;
    }

    this.uiService.displayMessage('Error', 'We could not save your insurance information. Please try again.');
  }

  getPatientInsuranceCard() {
    let promise = new Promise((resolve, reject) => {
      this.infoService.getPatientInsuranceCard(this.PatientId)
        .subscribe(resolve, reject);
    });
    return promise;
  }

  savePatientInsuranceCard() {
    let promise = new Promise((resolve, reject) => {
      if (!this.cardObj) {
        reject(new Error('The insurance card has not finished loading.'));
        return;
      }

      this.cardObj.InsuranceCardInfo = JSON.stringify(this.tmpCardObj);
      this.infoService.savePatientInsuranceCard(this.PatientId, this.cardObj)
        .subscribe((savedCard: any) => {
          // Adopt the persisted row so its audit stamp is current; without this a second save would be
          // rejected as a stale write by the server concurrency check.
          if (savedCard) {
            this.cardObj = savedCard;
          }
          resolve(savedCard);
        }, reject);
    });
    return promise;
  }

}
